import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import { useEffect } from 'react';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import debounce from 'react-utils/debounce';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import UIButton from 'UIComponents/button/UIButton';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISearchInput from 'UIComponents/input/UISearchInput';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
import useNavigate from '../../../hooks/useNavigate';
import { useUserOwnsPlaylist, useFullPlaylist } from '../../savedItems';
import { usePlaylistItemManager } from './PlaylistItemManager';
import produce from 'immer';
import { PLAYLIST_RESULTS_URL_FRAGMENT, RESULTS_URL_FRAGMENT } from '../../../constants/Navigation'; // @ts-expect-error Could not find a declaration file for module

import { useFetchFilters } from '../../../hooks/catalogPageHooks';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import styled from '../../../utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
export const SEARCH_QUERY_KEY = FILTER_TYPES.SEARCH;
export const PLAYLIST_ITEM_SEARCH_PATH = RESULTS_URL_FRAGMENT;

const noop = () => {};

const noopPromise = () => Promise.resolve();

const PlaylistItemSearchContext = /*#__PURE__*/createContext({
  query: new URLSearchParams(window.location.search).get(SEARCH_QUERY_KEY) || '',
  resetQuery: noop,
  setQueryAndNavigate: noop,
  playlistId: 0,
  newPlaylistItems: [],
  addPlaylistItem: noop,
  removePlaylistItem: noop,
  resetPlaylistItems: noop,
  saveNewPlaylistItems: noopPromise,
  resetAllState: noop,
  playlistLoading: false
});
export const PlaylistItemSearchProvider = ({
  children,
  playlistId
}) => {
  const history = useHistory();
  const location = useLocation();
  const {
    saveNewPlaylistItems: savePlaylistItems
  } = usePlaylistItemManager();
  const {
    playlist,
    playlistLoading
  } = useFullPlaylist(playlistId);
  const [query, setQuery] = useState(new URLSearchParams(window.location.search).get(SEARCH_QUERY_KEY) || '');
  const [newPlaylistItems, setPlaylistItems] = useState([]);
  useEffect(() => {
    if (playlist && playlist.items.length > 0) {
      setPlaylistItems(playlist.items.map(item => Object.assign({}, item, {
        hasBeenRemoved: false
      })));
    }
  }, [playlist]);
  const debouncedNavigate = useMemo(() => debounce(history.push, 400), [history.push]);
  const debouncedUsageTrack = useMemo(() => debounce((searchQuery = '') => {
    UsageTracker.track('playlistInteraction', {
      action: 'search-items',
      searchQuery
    });
  }, 400), []);
  const setQueryAndNavigate = useCallback(newValue => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(SEARCH_QUERY_KEY, newValue);
    setQuery(newValue);
    debouncedNavigate(Object.assign({}, location, {
      pathname: `${PLAYLIST_RESULTS_URL_FRAGMENT}`.replace(':playlistId', playlistId.toString()),
      search: searchParams.toString()
    }));
    debouncedUsageTrack(newValue);
  }, [debouncedNavigate, debouncedUsageTrack, location, playlistId]);
  const resetQuery = useCallback(() => {
    setQuery('');
  }, [setQuery]);
  const addPlaylistItem = useCallback(newOrUpdatedItem => {
    setPlaylistItems(items => produce(items, draft => {
      let foundItem = false;

      for (const item of draft) {
        if (item.itemId === newOrUpdatedItem.itemId && item.type === newOrUpdatedItem.type) {
          item.hasBeenRemoved = false;
          foundItem = true;
          break;
        }
      }

      if (!foundItem) draft.push(newOrUpdatedItem);
    }));
    UsageTracker.track('playlistInteraction', {
      action: 'add-to-playlist',
      location: 'playlist-items-search'
    });
  }, []);
  const removePlaylistItem = useCallback(itemToRemove => {
    setPlaylistItems(items => produce(items, draft => {
      for (const item of draft) {
        if (item.itemId === itemToRemove.itemId && item.type === itemToRemove.type) {
          item.hasBeenRemoved = true;
          break;
        }
      }
    }));
    UsageTracker.track('playlistInteraction', {
      action: 'remove-item-from-playlist',
      location: 'playlist-items-search'
    });
  }, []);
  const resetPlaylistItems = useCallback(() => {
    setPlaylistItems(playlist ? playlist.items.map(item => Object.assign({}, item, {
      hasBeenRemoved: false
    })) : []);
  }, [playlist]);
  const resetAllState = useCallback(() => {
    resetPlaylistItems();
    resetQuery();
    UsageTracker.track('playlistInteraction', {
      action: 'cancel-item-changes'
    });
  }, [resetPlaylistItems, resetQuery]);
  const saveNewPlaylistItems = useCallback(() => {
    return savePlaylistItems(newPlaylistItems).then(() => {
      resetQuery();
    });
  }, [newPlaylistItems, resetQuery, savePlaylistItems]);
  const contextState = useMemo(() => ({
    query,
    setQueryAndNavigate,
    resetQuery,
    playlistId,
    newPlaylistItems,
    addPlaylistItem,
    removePlaylistItem,
    saveNewPlaylistItems,
    resetPlaylistItems,
    resetAllState,
    playlistLoading
  }), [addPlaylistItem, newPlaylistItems, playlistId, query, removePlaylistItem, resetAllState, saveNewPlaylistItems, resetPlaylistItems, resetQuery, setQueryAndNavigate, playlistLoading]);
  return /*#__PURE__*/_jsx(PlaylistItemSearchContext.Provider, {
    value: contextState,
    children: children
  });
};
export const usePlaylistItemSearch = () => {
  const originalState = useContext(PlaylistItemSearchContext);
  return Object.assign({}, originalState, {
    numberOfSelectedItems: originalState.newPlaylistItems.filter(item => !item.hasBeenRemoved).length
  });
};
const SearchInput = styled(UISearchInput).withConfig({
  displayName: "PlaylistItemSearchManager__SearchInput",
  componentId: "i0es6i-0"
})(["", ""], ({
  $isLoading
}) => $isLoading && `[data-icon-name] { visibility: hidden}`);
const LoadingSpinner = styled(UILoadingSpinner).attrs({
  size: 'extra-small'
}).withConfig({
  displayName: "PlaylistItemSearchManager__LoadingSpinner",
  componentId: "i0es6i-1"
})(["position:absolute;top:2px;right:20px;"]);
export const PlaylistItemSearch = ({
  inputRef
}) => {
  const {
    query,
    setQueryAndNavigate
  } = usePlaylistItemSearch();
  const {
    tracksLessonsSearchQueryLoading: isLoading
  } = useFetchFilters();
  const handleChange = useCallback(e => {
    setQueryAndNavigate(e.target.value);
  }, [setQueryAndNavigate]);
  return /*#__PURE__*/_jsx(UIFormControl, {
    "aria-label": I18n.text('learningCenter.savedItems.playlistDetails.search'),
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(SearchInput, {
        $isLoading: isLoading,
        autoFocus: true,
        onChange: handleChange,
        placeholder: I18n.text('learningCenter.savedItems.playlistDetails.search'),
        ref: inputRef,
        value: query
      }), isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {})]
    })
  });
};
export const PlaylistItemSearchManager = ({
  listView,
  searchView
}) => {
  const {
    url
  } = useRouteMatch();
  const {
    playlistId
  } = usePlaylistItemSearch();
  const userOwnsPlaylist = useUserOwnsPlaylist(playlistId);
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsxs(Route, {
      path: `*${PLAYLIST_ITEM_SEARCH_PATH}`,
      children: [userOwnsPlaylist !== null && !userOwnsPlaylist && /*#__PURE__*/_jsx(Redirect, {
        to: url
      }), searchView]
    }), /*#__PURE__*/_jsx(Route, {
      children: listView
    })]
  });
};
export const CancelPlaylistSearchButton = () => {
  const {
    url
  } = useRouteMatch();
  const {
    resetAllState
  } = usePlaylistItemSearch();
  return /*#__PURE__*/_jsx(UIRouterButton, {
    onClick: resetAllState,
    to: url.split(PLAYLIST_ITEM_SEARCH_PATH)[0],
    use: "secondary",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.common.cancel"
    })
  });
};
export const SavePlaylistSearchButton = () => {
  const {
    url
  } = useRouteMatch();
  const navigate = useNavigate();
  const {
    saveNewPlaylistItems
  } = usePlaylistItemSearch();
  const handleClick = useCallback(() => {
    saveNewPlaylistItems().then(() => {
      navigate(url.split(PLAYLIST_ITEM_SEARCH_PATH)[0]);
    }).catch(() => {});
    UsageTracker.track('playlistInteraction', {
      action: 'save-item-changes'
    });
    /**
     * We have to add the logic for saving the playlist items added in search here
     */
  }, [navigate, saveNewPlaylistItems, url]);
  return /*#__PURE__*/_jsx(UIButton, {
    onClick: handleClick,
    use: "primary",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.common.save"
    })
  });
};